import { ScopedInstanceMember } from '../../shared/interfaces/scopeInstanceMember.interface';
import { bucketOf, BUCKET_ORDER, ScopeBucket } from './scopedMembers';

// Avatar geometry, shared by the cell that draws the stacks and the calculation
// that sizes the column for them. One definition of each so the two cannot drift.

// mgt-person draws a 24px avatar by default, and the people picker uses the same
// 24px for a selected person. Match it so scoping avatars line up with every
// other person image in the product. #34542
export const AVATAR_SIZE = 24;
// How far each avatar in a stack tucks under the one before it, as a share of the
// avatar itself, so the run stays proportional at any size.
export const AVATAR_OVERLAP_RATIO = 0.3;
// Avatars shown per bucket before the rest collapse into "+n".
export const MAX_DISPLAYED = 3;

// Gap between one bucket's avatar run and the next. The trigger uses Northstar's
// gap.small, which its teams theme resolves to 10px.
const BUCKET_GAP = 10;
// A bucket's own "+n": its 6px marginLeft plus a one or two digit count.
const OVERFLOW_TAIL_WIDTH = 26;
// The "+n" for whole buckets that did not fit. It sits directly in the trigger's
// Flex, so it takes the gap above rather than a margin of its own.
const COLLAPSED_TAIL_WIDTH = 20;
// A hairline between the last drawn bucket and that trailing count. Both counts
// read "n more people", but one means within this group and the other means in
// groups not drawn at all - sat next to each other in identical text they were
// indistinguishable, so the rule marks where drawn stops. #35321
export const DIVIDER_WIDTH = 1;
export const DIVIDER_HEIGHT = 16;
// Gap, rule, gap, then the count itself.
const COLLAPSED_TAIL_TOTAL = BUCKET_GAP + DIVIDER_WIDTH + BUCKET_GAP + COLLAPSED_TAIL_WIDTH;
// The word an unscoped record renders instead of any avatars, at size small.
// Measured across the locales rather than estimated: 48.5px for English "Everyone",
// 76.2px for the widest, French "Tout le monde". Inert while the column's own floor
// sits above it, but it is what any caller without a floor would get. #35321
const EVERYONE_WIDTH = 80;

// How wide this column is allowed to get before it starts collapsing buckets into
// a trailing count. Sized so the ordinary cases - a couple of small buckets - draw
// in full and only a heavily shared record collapses. Nothing is lost when it does:
// the hover panel still lists every member. #35321
export const AVAILABLE_TO_MAX_WIDTH = 250;

// The KB list stores ScopedMembers as a JSON string; every other caller holds the
// parsed array.
export const parseMembers = (scopedMembers: ScopedInstanceMember[] | string): ScopedInstanceMember[] => {
  if (!scopedMembers) return [];
  if (typeof scopedMembers !== 'string') return scopedMembers;
  try {
    return JSON.parse(scopedMembers) ?? [];
  } catch {
    return [];
  }
};

const stackWidth = (shown: number, avatarSize: number) =>
  shown * avatarSize - (shown - 1) * Math.round(avatarSize * AVATAR_OVERLAP_RATIO);

const bucketWidth = (count: number, maxDisplayed: number, avatarSize: number) =>
  stackWidth(Math.min(count, maxDisplayed), avatarSize) + (count > maxDisplayed ? OVERFLOW_TAIL_WIDTH : 0);

// Member counts per bucket in the order the cell draws them, empty buckets dropped.
export const bucketSizes = (scopedMembers: ScopedInstanceMember[] | string): number[] => {
  const counts = new Map<ScopeBucket, number>();
  parseMembers(scopedMembers).forEach(member => {
    const bucket = bucketOf(member.ScopedType);
    counts.set(bucket, (counts.get(bucket) ?? 0) + 1);
  });

  return BUCKET_ORDER.map(bucket => counts.get(bucket) ?? 0).filter(count => count > 0);
};

// What one Available To cell needs to draw in full. DetailsList sizes a column from
// its minWidth and maxWidth and never measures what was rendered, so a list that
// wants this column only as wide as what is really shared has to work the width out
// from the data. It can, because everything the cell draws is bounded: either the
// word "Everyone", or one avatar run per bucket with an optional "+n" tail. #35321
export const availableToWidth = (
  scopedMembers: ScopedInstanceMember[] | string,
  maxDisplayed: number = MAX_DISPLAYED,
  avatarSize: number = AVATAR_SIZE
): number => {
  const sizes = bucketSizes(scopedMembers);
  if (sizes.length === 0) return EVERYONE_WIDTH;

  const widths = sizes.map(count => bucketWidth(count, maxDisplayed, avatarSize));
  return widths.reduce((total, width) => total + width, 0) + (widths.length - 1) * BUCKET_GAP;
};

// How much of the cell fits in `budget`, and how many members are left over. Whole
// buckets are dropped from the end rather than part-drawn, and the members they held
// are counted into one trailing "+n" - so "+n" means the same thing wherever it
// appears in the cell, n more people, never n more groups. #35321
export const fitBuckets = (
  sizes: number[],
  budget: number,
  maxDisplayed: number = MAX_DISPLAYED,
  avatarSize: number = AVATAR_SIZE
): { shownBuckets: number; hiddenMembers: number } => {
  const widths = sizes.map(count => bucketWidth(count, maxDisplayed, avatarSize));
  const hiddenFrom = (shown: number) => sizes.slice(shown).reduce((total, count) => total + count, 0);

  for (let shown = widths.length; shown >= 1; shown--) {
    const drawn = widths.slice(0, shown).reduce((total, width) => total + width, 0) + (shown - 1) * BUCKET_GAP;
    const tail = shown < widths.length ? COLLAPSED_TAIL_TOTAL : 0;
    if (drawn + tail <= budget) return { shownBuckets: shown, hiddenMembers: hiddenFrom(shown) };
  }

  // Nothing fits, which needs a budget narrower than a single bucket. Draw one
  // anyway - an empty cell would read as "shared with nobody".
  return { shownBuckets: Math.min(1, widths.length), hiddenMembers: hiddenFrom(1) };
};

// The widest cell across a set of records, floored at the column's own minWidth and
// capped at the ceiling above.
//
// The floor is not cosmetic: DetailsList's justified layout grows a column by
// `Math.min(spaceLeft, maxWidth - minWidth)`, so a maxWidth below the minWidth
// yields a negative increment and shrinks the column past its own minimum. #35321
export const availableToColumnWidth = (
  records: { ScopedMembers?: ScopedInstanceMember[] | string }[],
  minWidth: number,
  maxWidth: number = AVAILABLE_TO_MAX_WIDTH
): number => Math.min(maxWidth, Math.max(minWidth, ...(records ?? []).map(record => availableToWidth(record?.ScopedMembers))));
